import React from 'react';
import { getDate, formatTime, formatTimeString } from './dateUtils';
import { replacePlaceholders } from './dataLogic';
import { zmanLoader } from '../calendar/zmanim';
import { bidiText } from './bidiText';
import ScheduleValue from '../components/ScheduleValue';

/**
 * Centralized utility for rendering schedule sections
 * Uses the kiosk logic as the source of truth
 */

/**
 * Renders a schedule section with consistent logic
 * @param {Object} section - The schedule section data
 * @param {string} sectionType - Type of section ('weekly' or 'dated')
 * @param {Array} zmanimData - Zmanim data for the current date
 * @param {Date} currentDate - Current date for zmanim resolution
 * @returns {JSX.Element|null} - The rendered section or null
 */
export function renderScheduleSection(section, sectionType = 'weekly', zmanimData = [], currentDate = null) {
  
  if (!section || !section.schedule) {
    return null;
  }

  const filteredSchedule = section.schedule.filter(item => !item.hideItem && item.display !== 'screen_only' && item.display !== 'none');

  return (
    <section className="service-schedule">
      <h2>{section.sectionTitle}</h2>
      {filteredSchedule.map((item, idx) => {
          // For weekly sections, values are already resolved
          // For dated sections, we need to resolve zmanim manually
          let displayValue = item.value || '';
                    
          
          if (sectionType === 'dated' && !displayValue) {
            // Resolve zmanim for dated sections
            let itemZmanimObj = null;
            let itemZmanimData = zmanimData;
            if (item.date) {
              const itemDate = new Date(item.date);
              itemZmanimObj = zmanLoader(itemDate);
              itemZmanimData = itemZmanimObj.zmanim;
            } else if (currentDate) {
              // Use the current date's zmanim object if no item date specified
              itemZmanimObj = zmanLoader(currentDate);
              itemZmanimData = itemZmanimObj.zmanim;
            }
            
            if (item.time) {
              displayValue = formatTimeString(item.time);
            } else if (item.zman) {
              // Handle special zmanim that are in zmanim array
              if (item.zman === 'MOLAD' || item.zman === 'SEVENTH_HOUR' || item.zman === 'FAST_END' || item.zman === 'FAST_START' || item.zman === 'TZEIS8_5') {
                const zman = itemZmanimData.find(z => z.zman === item.zman);
                if (zman && zman.time) {
                    displayValue = formatTime(zman.time);
                } else if (zman && zman.value) {
                  displayValue = zman.value; // Don't format value strings (they're already formatted)
                } else {
                  displayValue = item.zman; // Show the zman name if not found
                }
              } else {
                // For regular zmanim, try getZman first if we have the object, otherwise use array
                if (itemZmanimObj && itemZmanimObj.getZman) {
                  const time = itemZmanimObj.getZman(item.zman);
                  if (time) {
                    displayValue = formatTime(time);
                  }
                } else {
                  // Fallback to array search
                  const zman = itemZmanimData.find(z => z.zman === item.zman);
                  if (zman && zman.time) {
                    displayValue = formatTime(zman.time);
                  } else if (zman && zman.value) {
                    displayValue = zman.value;
                  }
                }
              }
            } else if (item.relativeZman) {
              // Handle relative zman calculations
              let baseTimeValue = null;
              
              // Try to get base zman using getZman method first (more reliable)
              if (itemZmanimObj && itemZmanimObj.getZman) {
                baseTimeValue = itemZmanimObj.getZman(item.relativeZman);
              }
              
              // Fallback to array search if getZman didn't work
              if (!baseTimeValue) {
                const baseZman = itemZmanimData.find(z => z.zman === item.relativeZman);
                if (baseZman) {
                  baseTimeValue = baseZman.time || baseZman.value;
                }
              }
              
              if (baseTimeValue) {
                // baseTimeValue is already a Date object if from getZman, or time/value from array
                const baseTime = baseTimeValue instanceof Date ? baseTimeValue : new Date(baseTimeValue);
                const minutes = item.relativeMinutes || 0;
                
                // Parse the direction and rounding (support both old and new formats)
                let direction = item.relativeDirection;
                let rounding = 'exact';
                
                // Check if we're using the new format with rounding
                if (item.relativeDirection && item.relativeDirection.includes('_')) {
                  const parts = item.relativeDirection.split('_');
                  direction = parts[0];
                  rounding = parts[1];
                }
                                  
                // Add or subtract minutes based on the direction
                const minutesInMs = minutes * 60 * 1000;
                const adjustedTime = new Date(baseTime.getTime());
                
                if (direction === 'before') {
                  adjustedTime.setTime(adjustedTime.getTime() - minutesInMs);
                } else {
                  adjustedTime.setTime(adjustedTime.getTime() + minutesInMs);
                }
                
                displayValue = formatTime(adjustedTime);
              } else {
                console.warn('scheduleRenderer - baseTimeValue not found:', {
                  itemTitle: item.title,
                  relativeZman: item.relativeZman,
                  availableZmanim: itemZmanimData.map(z => z.zman)
                });
              }
            }
          }
          
          // Process placeholders in the value field
          if (displayValue) {
            displayValue = replacePlaceholders(displayValue, currentDate || getDate());
          }
          
          // Format the time if it appears to be a time value (contains numbers and possibly am/pm)
          if (displayValue && /\d+:\d+/.test(displayValue)) {
            // Make sure there's a space before am/pm
            displayValue = displayValue.replace(/(\d+:\d+)([ap]m)/i, '$1 $2');
          }
                    
          // Check if the display value contains Hebrew characters
          const hasHebrew = /[\u0590-\u05FF]/.test(displayValue);
          
          return (
            <div key={idx} className="schedule-item">
              <div className="schedule-title-container">
                <strong className="schedule-title">{bidiText(item.title)}</strong>
                {item.byline && (
                  <div className="schedule-byline">
                    {bidiText(item.byline)}
                  </div>
                )}
              </div>
              <ScheduleValue
                className="schedule-value"
                value={displayValue}
                fontSize={item.fontSize}
                data-hebrew={hasHebrew ? 'true' : 'false'}
              >
                {bidiText(displayValue)}
              </ScheduleValue>
            </div>
          );
        })}
    </section>
  );
}

/**
 * Renders a schedule section for kiosk (simplified version)
 * @param {Object} printSchedule - The print schedule data
 * @param {string} sectionKey - The key for the section to render
 * @returns {JSX.Element|null} - The rendered section or null
 */
export function renderKioskScheduleSection(printSchedule, sectionKey) {
  if (!printSchedule.fullWeeklySchedules || 
      !printSchedule.fullWeeklySchedules[sectionKey] || 
      !printSchedule.fullWeeklySchedules[sectionKey].schedule) {
    return null;
  }

  const section = printSchedule.fullWeeklySchedules[sectionKey];
  return renderScheduleSection(section, 'weekly');
}
